import React, { useState } from 'react';
import type { CrudAddModalProps } from '../crud/CrudTable';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import {
	Button,
	Container,
	Group,
	Loader,
	MultiSelect,
	NumberInput,
	Select,
	Stack,
	TextInput,
	Textarea,
} from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import z from 'zod';
import type { CouponListFragment, CouponSingleFragment, ProductListFragment } from '../../graphql/graphql';
import { CouponDiscountType } from '../../graphql/graphql';
import { useCouponService } from '../../services/CouponService';
import { IconCurrencyDollar, IconPercentage } from '@tabler/icons-react';
import { useProductService } from '../../services/ProductService';
import useLoadingEffect from '@dr-pam/common-components/Hooks/useLoadingEffect';
import { openConfirmModal } from '../modals/ConfirmModal';

const validationSchema = z
	.object({
		code: z.string().nonempty('Code is required').max(512),
		description: z.string().max(512),
		discountType: z.string().nonempty(),
		percentage: z.coerce.string().length(0).or(z.coerce.number().min(0).max(100)),
		fixedAmount: z.coerce.string().length(0).or(z.coerce.number().positive()),
		restrictedToProducts: z.array(z.string()).min(1),
	})
	.refine(
		(data) => {
			if (data.discountType === CouponDiscountType.Percentage) {
				if (data.percentage !== '') {
					const parsed = parseFloat(`${data.percentage}`);
					const isValid = !isNaN(parsed) && parsed >= 0 && parsed <= 100;
					return isValid;
				}
				return false;
			}
			return true;
		},
		{
			message: 'Invalid percentage',
			path: ['percentage'],
		},
	)
	.refine(
		(data) => {
			if (data.discountType === CouponDiscountType.FixedAmount) {
				if (data.fixedAmount !== '') {
					const parsed = parseFloat(`${data.fixedAmount}`);
					const isValid = !isNaN(parsed) && parsed >= 0;
					console.log(parsed, isValid);
					return isValid;
				}
				return false;
			}
			return true;
		},
		{
			message: 'Invalid amount',
			path: ['fixedAmount'],
		},
	);

export type AddCouponModalProps = CrudAddModalProps<CouponListFragment, CouponSingleFragment>;

export default function AddCouponModal(props: AddCouponModalProps) {
	const { modalId, onCancel, onCreated } = props;

	const { addLoader, removeLoader, isLoading } = useLoadTracker();

	const couponService = useCouponService();
	const productService = useProductService();

	const [products, setProducts] = useState<ProductListFragment[]>([]);

	const isLoadingProducts = useLoadingEffect(async () => {
		const request = productService.getAll();
		const products = await request.response;
		setProducts(products);
	}, [productService]);

	const form = useForm<AddCouponForm>({
		initialValues: {
			code: '',
			description: '',
			discountType: CouponDiscountType.Percentage,
			fixedAmount: '',
			percentage: '',
			restrictedToProducts: [],
		},
		validate: schemaResolver(validationSchema),
	});

	const handleSubmit = async (values: AddCouponForm) => {
		const request = couponService.getByCode(values.code);
		const coupon = await request.response;

		if (coupon && coupon.isArchived) {
			await openConfirmModal({
				title: 'Coupon code reused',
				danger: true,
				children: (
					<p>
						A coupon with the code <strong>{values.code}</strong> used to exist but has been deactivated.{' '}
						<br /> <br />
						By continuing, you will override details of the old coupon. Click <strong>
							Update Coupon
						</strong>{' '}
						to continue.
					</p>
				),
				confirmText: 'Update Coupon',
				onConfirm: async () => await handleCreateCoupon(values, coupon),
			});
		} else {
			await handleCreateCoupon(values);
		}
	};

	const handleCreateCoupon = async (values: AddCouponForm, coupon?: CouponSingleFragment) => {
		const loader = addLoader();
		let couponInstance: CouponSingleFragment | null | undefined = coupon;

		try {
			if (couponInstance) {
				couponInstance = await couponService.update(coupon?.id ?? '', {
					isPublished: { set: false }, // Force it to draft status
					isArchived: { set: false },
					description: { set: values.description },
					discountType: { set: values.discountType },
					percentage: {
						set:
							values.discountType === CouponDiscountType.Percentage
								? parseFloat(`${values.percentage}`) * 0.01
								: null,
					},
					fixedAmountInCents: {
						set:
							values.discountType === CouponDiscountType.FixedAmount
								? parseFloat(`${values.fixedAmount}`) * 100
								: null,
					},
					restrictedToProducts: {
						deleteMany: [
							{
								couponId: {
									equals: coupon?.id,
								},
							},
						],
						create: values.restrictedToProducts.map((id) => {
							return {
								product: {
									connect: {
										id,
									},
								},
							};
						}),
					},
				});
			} else {
				couponInstance = await couponService.create({
					code: values.code,
					description: values.description,
					discountType: values.discountType,
					percentage:
						values.discountType === CouponDiscountType.Percentage
							? parseFloat(`${values.percentage}`) * 0.01
							: null,
					fixedAmountInCents:
						values.discountType === CouponDiscountType.FixedAmount
							? parseFloat(`${values.fixedAmount}`) * 100
							: null,
					restrictedToProducts: {
						create: values.restrictedToProducts.map((id) => {
							return {
								product: {
									connect: {
										id,
									},
								},
							};
						}),
					},
				});
			}
			onCreated(couponInstance);
			NotificationUtils.showSuccess('Successfully created coupon', couponInstance.code);
			modals.close(modalId);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to create coupon');
			removeLoader(loader);
		}
	};

	const handleCancel = () => {
		onCancel?.();
		modals.close(modalId);
	};

	const handleProductSubscriptionsChanged = (productNames: string[]) => {
		const selectedProducts = products.filter((product) => productNames.includes(product.name));
		form.setFieldValue(
			'restrictedToProducts',
			selectedProducts.map((product) => product.id),
		);
	};

	const productsData = products.map((product) => product.name);
	const selectedProductsValue = form.values.restrictedToProducts
		.map((productId) => {
			const product = products.find((product) => product.id === productId);
			return product?.name;
		})
		.filter((name) => name !== undefined) as string[];

	const isAnyLoading = isLoading || isLoadingProducts;

	return (
		<Container>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack>
					<TextInput
						{...form.getInputProps('code')}
						type="code"
						label="Discount Code"
						description="The code to enter during checkout"
						disabled={isAnyLoading}
						withAsterisk
					/>
					<Textarea
						{...form.getInputProps('description')}
						label="Description"
						description="A description of the coupon"
						disabled={isAnyLoading}
					/>
					<Select
						{...form.getInputProps('discountType')}
						data={Object.values(CouponDiscountType)}
						label="Discount Type"
						description="The type of discount to apply"
						placeholder={'--- Please select ---'}
						disabled={isAnyLoading}
						withAsterisk
					/>
					{form.values.discountType === CouponDiscountType.Percentage ? (
						<NumberInput
							{...form.getInputProps('percentage')}
							label="Percentage Discount"
							description="The percentage discount amount (0 - 100)"
							min={0}
							max={100}
							step={1}
							disabled={isAnyLoading}
							rightSection={<IconPercentage />}
							withAsterisk
						/>
					) : (
						<NumberInput
							{...form.getInputProps('fixedAmount')}
							label="Fixed Discount Price (AUD)"
							description="The discount amount in dollars"
							min={0}
							decimalScale={2}
							decimalSeparator="."
							thousandSeparator=","
							disabled={isAnyLoading}
							leftSection={<IconCurrencyDollar />}
							withAsterisk
						/>
					)}
					<MultiSelect
						{...form.getInputProps('restrictedToProducts')}
						label="Assigned to Products"
						description="The products this coupon can be used for"
						data={productsData}
						value={selectedProductsValue}
						onChange={handleProductSubscriptionsChanged}
						disabled={isAnyLoading}
						withAsterisk
					/>
					<Group justify="flex-end" mt="md">
						<Button type="button" variant="subtle" onClick={handleCancel} disabled={isAnyLoading}>
							Cancel
						</Button>
						<Button type="submit" disabled={isAnyLoading}>
							Add coupon {isAnyLoading && <Loader size="xs" ml="xs" />}
						</Button>
					</Group>
				</Stack>
			</form>
		</Container>
	);
}

export type AddCouponForm = {
	code: string;
	description: string;
	discountType: CouponDiscountType;
	percentage: number | '';
	fixedAmount: number | '';
	restrictedToProducts: string[];
};
